import { gql } from '@apollo/client';
import {
  GraphQLEnumType,
  GraphQLInputType,
  GraphQLList,
  GraphQLNonNull,
  GraphQLScalarType,
  GraphQLSchema,
  OperationDefinitionNode,
} from 'graphql';

import { printType } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { assertUnreachable } from 'src/lib/assertUnreachable';

interface ListType {
  kind: 'ListType';
  type: Type;
}
interface NonNullType {
  kind: 'NonNullType';
  type: Type;
}
interface NamedType {
  kind: 'NamedType';
  name: {
    kind: string;
    value: string;
  };
}
type Type = ListType | NonNullType | NamedType;

export const getVariableShapeForReference = (
  type: Type,
  schema: GraphQLSchema,
): FieldShape => {
  switch (type.kind) {
    case 'NonNullType': {
      const unwrapped = getVariableShapeForReference(type.type, schema);
      return {
        ...unwrapped,
        nonNull: true,
        type: `${unwrapped.type}!`,
      };
    }
    case 'ListType': {
      const unwrapped = getVariableShapeForReference(type.type, schema);
      return {
        kind: 'array',
        nonNull: false,
        type: `[${unwrapped.type}]`,
        innerType: unwrapped,
      };
    }
    case 'NamedType':
      return {
        type: type.name.value,
        nonNull: false,
        ...getVariableShape(
          schema.getType(type.name.value) as GraphQLInputType,
          schema,
        ),
      };
    default:
      return assertUnreachable(type);
  }
};

/**
 * Stripped down model of input types and the root variables
 */
export type VariableShape =
  | {
      kind: 'string' | 'number' | 'boolean' | 'undefined';
    }
  | {
      kind: 'enum';
      enumValues: Array<string>;
    }
  | {
      kind: 'array';
      innerType: VariableShape;
    }
  | (VariableShapes & { key: string });
export type FieldShape = VariableShape & {
  type: string;
  documentation?: string;
  nonNull: boolean;
};
export interface VariableShapes {
  kind: 'object';
  fields: {
    [key: string]: FieldShape;
  };
}

const variableCache: Record<
  string,
  (VariableShapes & { key: string }) | undefined
> = {};
const getVariableShape = (
  typeNode: GraphQLInputType,
  schema: GraphQLSchema,
): VariableShape => {
  if (typeNode instanceof GraphQLNonNull) {
    return getVariableShape(typeNode.ofType, schema);
  }
  if (typeNode instanceof GraphQLScalarType) {
    switch (typeNode.name) {
      case 'String':
      case 'ID':
        return { kind: 'string' };
      case 'Int':
      case 'Float':
        return { kind: 'number' };
      case 'Boolean':
        return { kind: 'boolean' };
      default:
        return { kind: 'undefined' };
    }
  }
  if (typeNode instanceof GraphQLEnumType) {
    return {
      kind: 'enum',
      enumValues: typeNode.getValues().map((enumValue) => enumValue.name),
    };
  }
  if (typeNode instanceof GraphQLList) {
    return {
      kind: 'array',
      innerType: getVariableShape(typeNode.ofType, schema),
    };
  }
  // typeNode is GraphQLInputObjectType
  const existing = variableCache[typeNode.name];
  if (existing) return existing;

  const variableShapes: VariableShapes & { key: string } = {
    key: typeNode.name,
    kind: 'object',
    fields: {},
  };
  variableCache[typeNode.name] = variableShapes;
  Object.entries(typeNode.getFields()).forEach(([fieldName, fieldType]) => {
    variableShapes.fields[fieldName] = {
      type: printType(fieldType.type),
      nonNull: fieldType.type instanceof GraphQLNonNull,
      ...getVariableShape(fieldType.type, schema),
    };
  });
  return variableShapes;
};

export const getVariableShapes = (
  schema: GraphQLSchema,
  operationString: string,
): VariableShapes[] | null => {
  let allVariableShapes: VariableShapes[] = [];
  const seenShapes = new Set();
  try {
    gql(operationString)
      .definitions.filter(
        (definition): definition is OperationDefinitionNode => {
          return definition.kind === 'OperationDefinition';
        },
      )
      .forEach(({ variableDefinitions }) => {
        const variableShapes: VariableShapes = {
          kind: 'object',
          fields: {},
        };
        variableDefinitions?.forEach(({ variable, type }) => {
          const name = variable.name.value;
          const variableShape = getVariableShapeForReference(type, schema);
          const shapeTag = `${name}: ${variableShape.type}`;
          // If this shape has been seen for this key in a previous operation,
          // dont add here
          if (!seenShapes.has(shapeTag)) {
            seenShapes.add(shapeTag);
            variableShapes.fields[name] = variableShape;
          }
        });
        allVariableShapes = [...allVariableShapes, variableShapes];
      });
  } catch (e) {
    return null;
  }
  return allVariableShapes;
};
